import { useEffect, useMemo, useRef, useState } from 'react';
import { aws } from '../lib/awsdb';
import { downloadXlsx } from '../lib/exportXlsx';
import { IconHistory, IconDownload, IconFilter } from '../ui/icons';
import { CATEGORIES, labelFor, colorFor } from './changeTypes';
import { formatDate, formatTime } from './time';
import WebChangeModal from './WebChangeModal';
import WebChangeActivityModal from './WebChangeActivityModal';
import ManageProjectsModal from './ManageProjectsModal';

const cardStyle = { background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-card)' };

function AccentButton({ onClick, children, color = '#6b7280' }) {
  const soft = `${color}14`;
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: `1px solid ${color}`, cursor: 'pointer', fontFamily: 'inherit', background: soft, color, boxShadow: 'var(--shadow-xs)', transition: 'background 150ms var(--ease-default), color 150ms var(--ease-default), transform 150ms var(--ease-default)' }}
      onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = soft; e.currentTarget.style.color = color; e.currentTarget.style.transform = 'translateY(0)'; }}>
      {children}
    </button>
  );
}

function Badge({ label, color }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, background: `${color}16`, color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
    <span style={{ width: 6, height: 6, borderRadius: 999, background: color }} />{label}
  </span>;
}

// Web Changes — a multi-project changelog (frontend-first, mock data). The 6
// data functions below are the single swap point for the later backend phase;
// today they mutate local state and append an activity entry on each mutation.
export default function WebChanges({ session }) {
  const isAdmin = session.role === 'admin';
  const [projects, setProjects] = useState([]);
  const [changes, setChanges] = useState([]);
  const [activity, setActivity] = useState([]);

  const [activeProject, setActiveProject] = useState(null); // null until first load → defaults to FON
  const [typeFilter, setTypeFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('changed_at');
  const [sortDir, setSortDir] = useState('desc');

  const [editing, setEditing] = useState(false); // change | null (new) | false (closed)
  const [activityOpen, setActivityOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);

  const projById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const projName = id => projById[id]?.name || '—';
  const initedRef = useRef(false);

  // --- data functions (PostgREST; no dedicated Lambda) ---
  async function loadAll() {
    const [pr, ch, ac] = await Promise.all([
      aws.from('web_change_projects').select('*').order('sort_order', { ascending: true }),
      aws.from('web_changes').select('*').is('archived_at', null).order('changed_at', { ascending: false }),
      aws.from('web_change_activity').select('*').order('created_at', { ascending: false }).limit(300),
    ]);
    const projs = pr.data || [];
    setProjects(projs);
    setChanges(ch.data || []);
    setActivity(ac.data || []);
    if (!initedRef.current && projs.length) {
      const fon = projs.find(p => p.name === 'FON');
      setActiveProject(fon ? fon.id : 'all'); // FON is the default project
      initedRef.current = true;
    }
  }
  useEffect(() => { loadAll(); }, []);

  async function logDb(entry) {
    try {
      await aws.from('web_change_activity').insert([{
        project_id: entry.project_id || null,
        action_type: entry.action_type,
        target: entry.target || null,
        detail: entry.detail || null,
        actor_code: session.code || null,
        actor_name: session.name || 'Someone',
      }]);
    } catch (_) { /* activity is best-effort — never block the mutation */ }
  }

  async function addChange(row) {
    try {
      await aws.from('web_changes').insert([{ ...row, created_by: session.name }]);
      await logDb({ action_type: 'change_created', target: projName(row.project_id), detail: row.action, project_id: row.project_id });
      loadAll();
    } catch (e) { alert('Could not save change: ' + (e.message || e)); }
  }
  async function updateChange(row) {
    try {
      const { id, ...fields } = row;
      await aws.from('web_changes').update({ ...fields, updated_by: session.name, updated_at: new Date().toISOString() }).eq('id', id);
      await logDb({ action_type: 'change_edited', target: projName(row.project_id), detail: row.action, project_id: row.project_id });
      loadAll();
    } catch (e) { alert('Could not update change: ' + (e.message || e)); }
  }
  async function archiveChange(c) {
    if (!confirm('Archive this change? It drops off the list.')) return;
    try {
      await aws.from('web_changes').update({ archived_at: new Date().toISOString(), archived_by: session.name }).eq('id', c.id);
      await logDb({ action_type: 'change_archived', target: projName(c.project_id), detail: c.action, project_id: c.project_id });
      loadAll();
    } catch (e) { alert('Could not archive: ' + (e.message || e)); }
  }
  async function addProject(row) {
    try {
      await aws.from('web_change_projects').insert([{ ...row, created_by: session.name }]);
      await logDb({ action_type: 'project_created', target: row.name });
      loadAll();
    } catch (e) { alert('Could not add project: ' + (e.message || e)); }
  }
  async function updateProject(id, patch) {
    try {
      await aws.from('web_change_projects').update(patch).eq('id', id);
      await logDb({ action_type: patch.active === false ? 'project_deactivated' : 'project_edited', target: projById[id]?.name, project_id: id });
      loadAll();
    } catch (e) { alert('Could not update project: ' + (e.message || e)); }
  }

  function onSaveChange(row, isEdit) { isEdit ? updateChange(row) : addChange(row); }

  function setSort(key) {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir(key === 'changed_at' ? 'desc' : 'asc'); }
  }

  // active-project scope (before type/search), for the type pill counts
  const scoped = useMemo(() => (!activeProject || activeProject === 'all') ? changes : changes.filter(c => c.project_id === activeProject), [changes, activeProject]);
  const typeCounts = useMemo(() => { const m = {}; scoped.forEach(c => { m[c.change_type] = (m[c.change_type] || 0) + 1; }); return m; }, [scoped]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = scoped.filter(c => {
      if (typeFilter && c.change_type !== typeFilter) return false;
      if (q) {
        const hay = `${c.action || ''} ${c.notes || ''} ${c.requested_by || ''} ${c.action_by || ''} ${projName(c.project_id)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === 'change_type') { av = labelFor(a.change_type); bv = labelFor(b.change_type); }
      else if (sortKey === 'project') { av = projName(a.project_id); bv = projName(b.project_id); }
      else { av = a.changed_at; bv = b.changed_at; }
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return list;
  }, [scoped, typeFilter, search, sortKey, sortDir, projById]);

  const showProjectCol = activeProject === 'all';

  function exportXlsx() {
    const cols = [
      ...(showProjectCol ? [{ label: 'Project', width: 16, value: r => projName(r.project_id) }] : []),
      { label: 'Date', width: 14, value: r => formatDate(r.changed_at) },
      { label: 'Time', width: 10, value: r => formatTime(r.changed_at) },
      { label: 'Type', width: 16, value: r => labelFor(r.change_type) },
      { label: 'Action', width: 60, value: r => r.action || '' },
      { label: 'Requested by', width: 16, value: r => r.requested_by || '' },
      { label: 'Action by', width: 16, value: r => r.action_by || '' },
      { label: 'Notes', width: 30, value: r => r.notes || '' },
    ];
    const label = activeProject === 'all' ? 'All' : projName(activeProject);
    downloadXlsx(`web-changes-${label}.xlsx`, 'Web Changes', cols, rows);
  }

  const th = (key, label, extra = {}) => (
    <th onClick={key ? () => setSort(key) : undefined}
      style={{ textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-subdued)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', padding: '9px 14px', borderBottom: '1px solid var(--border-subdued)', whiteSpace: 'nowrap', cursor: key ? 'pointer' : 'default', userSelect: 'none', position: 'sticky', top: 0, background: 'var(--surface-elevated)', ...extra }}>
      {label}{key && sortKey === key ? <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span> : ''}
    </th>
  );
  const td = { padding: '10px 14px', borderBottom: '1px solid var(--border-subdued)', verticalAlign: 'top', fontSize: 12.5 };

  return (
    <div className="app-screen">
      <div className="main-content">
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-emphasized)', margin: '0 0 4px', letterSpacing: 'var(--tracking-tight)' }}>Web Changes</h1>
            <p style={{ fontSize: 13, color: 'var(--text-subdued)', margin: 0 }}>Every website & store change, per project — searchable and logged</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <AccentButton color="#2563eb" onClick={() => setActivityOpen(true)}><IconHistory size={12} /> Activity Log</AccentButton>
            <AccentButton color="#6b7280" onClick={exportXlsx}><IconDownload size={12} /> Export</AccentButton>
            <button className="btn btn-primary btn-sm" onClick={() => setEditing(null)} style={{ whiteSpace: 'nowrap' }}>+ New Change</button>
          </div>
        </div>

        {/* project switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <ProjectPill label="All Projects" active={activeProject === 'all'} onClick={() => setActiveProject('all')} />
          {projects.filter(p => p.active).map(p => (
            <ProjectPill key={p.id} label={p.name} color={p.color} active={activeProject === p.id} onClick={() => setActiveProject(p.id)} />
          ))}
          {isAdmin && (
            <button type="button" onClick={() => setProjectsOpen(true)}
              style={{ marginLeft: 4, fontSize: 12, color: 'var(--text-subdued)', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer' }}>
              ⚙ Manage Projects
            </button>
          )}
        </div>

        {/* type filter pills + search */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <TypePill label={`All (${scoped.length})`} active={!typeFilter} onClick={() => setTypeFilter(null)} />
            {CATEGORIES.filter(c => typeCounts[c.key]).map(c => (
              <TypePill key={c.key} label={`${c.label} (${typeCounts[c.key]})`} color={colorFor(c.key)} active={typeFilter === c.key} onClick={() => setTypeFilter(typeFilter === c.key ? null : c.key)} />
            ))}
          </div>
          <input type="text" placeholder="Search changes..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '7px 12px', fontSize: 12, border: '1px solid var(--border-default)', borderRadius: 8, width: 200, fontFamily: 'inherit', color: 'var(--text-emphasized)', background: 'var(--surface-input)' }}
            onFocus={e => { e.currentTarget.style.boxShadow = 'var(--shadow-focus)'; e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
            onBlur={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border-default)'; }} />
        </div>

        {/* table */}
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>
              <div style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--surface-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: 'var(--text-disabled)' }}><IconFilter size={16} /></div>
              No changes match — adjust filters or log a new change.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {showProjectCol && th('project', 'Project')}
                    {th('changed_at', 'Date')}
                    {th(null, 'Time')}
                    {th('change_type', 'Type')}
                    {th(null, 'Action', { width: '40%' })}
                    {th(null, 'Requested by')}
                    {th(null, 'Action by')}
                    {th(null, 'Notes')}
                    <th style={{ borderBottom: '1px solid var(--border-subdued)', position: 'sticky', top: 0, background: 'var(--surface-elevated)' }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(c => (
                    <tr key={c.id} onClick={() => setEditing(c)}
                      style={{ cursor: 'pointer', transition: 'background 120ms var(--ease-default)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-default)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {showProjectCol && <td style={td}><Badge label={projName(c.project_id)} color={projById[c.project_id]?.color || '#64748b'} /></td>}
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-emphasized)', fontWeight: 500 }}>{formatDate(c.changed_at)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-subdued)' }}>{formatTime(c.changed_at)}</td>
                      <td style={td}><Badge label={labelFor(c.change_type)} color={colorFor(c.change_type)} /></td>
                      <td style={{ ...td, color: 'var(--text-emphasized)' }}>{c.action}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-subdued)' }}>{c.requested_by || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-subdued)' }}>{c.action_by || '—'}</td>
                      <td style={{ ...td, color: 'var(--text-subdued)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.notes || ''}>{c.notes || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 9 }} onClick={e => { e.stopPropagation(); archiveChange(c); }}>Archive</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-disabled)', marginTop: 10 }}>{rows.length} change{rows.length === 1 ? '' : 's'} shown</div>
      </div>

      {editing !== false && (
        <WebChangeModal session={session} change={editing} projects={projects}
          defaultProjectId={activeProject === 'all' ? undefined : activeProject}
          onClose={() => setEditing(false)} onSave={onSaveChange} />
      )}
      {activityOpen && <WebChangeActivityModal activity={activity} projects={projects} onClose={() => setActivityOpen(false)} />}
      {projectsOpen && isAdmin && <ManageProjectsModal projects={projects} onClose={() => setProjectsOpen(false)} onAddProject={addProject} onUpdateProject={updateProject} />}
    </div>
  );
}

function ProjectPill({ label, color, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
        border: active ? '1px solid var(--text-emphasized)' : '1px solid var(--border-default)',
        background: active ? 'var(--surface-inverted)' : '#fff', color: active ? '#fff' : 'var(--text-default)' }}>
      {color && <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />}
      {label}
    </button>
  );
}

function TypePill({ label, color = '#64748b', active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', fontSize: 11.5, fontWeight: 600, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
        border: `1px solid ${active ? color : 'var(--border-default)'}`,
        background: active ? `${color}18` : '#fff', color: active ? color : 'var(--text-subdued)' }}>
      {label}
    </button>
  );
}
